// SPDX-License-Identifier: GPL-3.0-or-later
// Feedback events: what the player should see and hear. The rules, the engine bridge and the input
// bridge emit them. The VFX, the sound and the HUD popups read them. An event only reports what
// happened. It never changes physics or rules, and no reader can change a game outcome.
//
// Zero imports: the server and the client type checks both read this file.
//
// Every field except `kind` is optional on purpose. A reader uses the fields that its effect needs
// and ignores the rest. `pos` is shared and read-only: do not change it. It is the position of the
// table element (table units, x right, y down the table, z up), not of the ball.

/** A point in table units. Same shape as `Vec3` in contracts.ts. */
export interface FeedbackPos {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Fields of every feedback event. */
export interface FeedbackBase {
  /** Name of the table element (an `ITEM` name), when the event comes from one. */
  readonly element?: string;
  /** Position of that element. Shared, read-only. */
  readonly pos?: FeedbackPos;
  /** How hard, 0..1 (from the contact speed). 1 is a full-strength hit. */
  readonly strength?: number;
  /** Points that the event is worth. */
  readonly scoreDelta?: number;
  /** A short label for a popup (a score reason, a mode name). Plain text, no markup. */
  readonly label?: string;
  /** The character (a `DECORATION` name) that reacts to this event. */
  readonly characterId?: string;
}

type Ev<K extends string, F extends object = object> = FeedbackBase & { readonly kind: K } & F;

/** One feedback event. Switch on `kind`. */
export type FeedbackEvent =
  // Contacts and shots (from the rules).
  | Ev<"bumper">
  | Ev<"sling">
  | Ev<"target">
  | Ev<"drop_bank_complete">
  | Ev<"spinner">
  | Ev<"ramp">
  | Ev<"orbit">
  | Ev<"lane">
  | Ev<"kicker">
  | Ev<"drain">
  // Table moments (from the rules).
  | Ev<"jackpot", { readonly superJackpot: boolean }>
  | Ev<"combo">
  | Ev<"skill_shot">
  | Ev<"ball_lock", { readonly count: number }>
  | Ev<"multiball_start", { readonly balls: number }>
  | Ev<"tilt_warning", { readonly count: number }>
  | Ev<"tilt">
  | Ev<"extra_ball">
  // The run (from the run director; the page emits `level_best`). A stage number is 1-based.
  // `choices` holds the perk ids of the 3 draft cards. A card with no perk is null.
  | Ev<"stage_start", { readonly stage: number; readonly label: string }>
  | Ev<"stage_clear", { readonly stage: number }>
  | Ev<"draft_open", { readonly choices: readonly [string | null, string | null, string | null] }>
  | Ev<"perk_pick", { readonly perkId: string }>
  | Ev<"run_clear">
  | Ev<"hurry_up_start", { readonly seconds: number }>
  | Ev<"hurry_up_collect">
  | Ev<"frenzy_start", { readonly seconds: number }>
  | Ev<"combo_chain", { readonly count: number }>
  | Ev<
      "timed_event_end",
      { readonly event: "hurry_up" | "frenzy" | "combo_chain"; readonly collected: boolean }
    >
  | Ev<"extra_ball_lit">
  | Ev<"level_best", { readonly stage: number; readonly score: number }>
  // The boss of a stage (from the run director). `anchor` is the id of the table target group
  // that the boss stands on. `phase` is 0 to 3. A boss attack is a shield slam: a wind-up
  // (`boss_telegraph`), then the shield (`boss_shield` with `on` true). The shield blocks damage.
  // A boss attack never drains a ball, locks a flipper or takes score.
  | Ev<
      "boss_start",
      {
        readonly name: string;
        readonly stage: number;
        readonly hp: number;
        readonly maxHp: number;
        readonly anchor: string;
      }
    >
  | Ev<
      "boss_hit",
      {
        readonly damage: number;
        readonly hp: number;
        readonly maxHp: number;
        readonly blocked: boolean;
        readonly source: "bumper" | "target" | "ramp" | "orbit";
      }
    >
  | Ev<"boss_phase", { readonly phase: number; readonly hp: number; readonly maxHp: number }>
  | Ev<"boss_telegraph", { readonly ms: number }>
  | Ev<
      "boss_shield",
      { readonly on: boolean; readonly ms: number; readonly cause: "phase" | "slam" }
    >
  | Ev<"boss_defeat", { readonly name: string; readonly stage: number }>
  // Game flow (from the engine bridge). `high_score` and `featured_turn_start` have no emitter
  // yet: the page owns the high score and the turn. The kinds are here for code that handles
  // them when the page sends them.
  | Ev<"ball_start", { readonly ballNumber: number }>
  | Ev<"ball_save">
  | Ev<"score">
  | Ev<"mode_start", { readonly modeId: string }>
  | Ev<"mode_complete", { readonly modeId: string }>
  | Ev<"game_over", { readonly finalScore: number }>
  | Ev<"high_score", { readonly score: number }>
  | Ev<"featured_turn_start">
  // Player input (from the input bridge).
  | Ev<"plunger_charge">
  | Ev<"plunger_launch">
  | Ev<"flipper_up">
  | Ev<"flipper_down">;

/** The kind of an event. */
export type FeedbackKind = FeedbackEvent["kind"];

/** Every kind, for a test or a reader that wants the whole list. */
export const FEEDBACK_KINDS: readonly FeedbackKind[] = [
  "bumper",
  "sling",
  "target",
  "drop_bank_complete",
  "spinner",
  "ramp",
  "orbit",
  "lane",
  "kicker",
  "jackpot",
  "combo",
  "skill_shot",
  "ball_lock",
  "multiball_start",
  "tilt_warning",
  "tilt",
  "extra_ball",
  "stage_start",
  "stage_clear",
  "draft_open",
  "perk_pick",
  "run_clear",
  "hurry_up_start",
  "hurry_up_collect",
  "frenzy_start",
  "combo_chain",
  "timed_event_end",
  "extra_ball_lit",
  "level_best",
  "boss_start",
  "boss_hit",
  "boss_phase",
  "boss_telegraph",
  "boss_shield",
  "boss_defeat",
  "ball_start",
  "ball_save",
  "drain",
  "score",
  "mode_start",
  "mode_complete",
  "game_over",
  "high_score",
  "featured_turn_start",
  "plunger_charge",
  "plunger_launch",
  "flipper_up",
  "flipper_down",
];

/** The event of one kind. */
export type FeedbackEventOf<K extends FeedbackKind> = Extract<FeedbackEvent, { kind: K }>;

/** A ball speed (table units per physics step) that counts as a full-strength hit. */
export const FULL_STRENGTH_SPEED = 35;

/** Turn a ball speed into a strength from 0 to 1. A speed that is not finite gives 0. */
export function strengthFromSpeed(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return 0;
  return Math.min(1, speed / FULL_STRENGTH_SPEED);
}
